'use client';

import clsx from 'clsx';
import { setCookie } from 'cookies-next';
import Link, { type LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';

import { useIsCampaignPageType } from '@/app/providers/campaign-type';
import { Icon, ContentfulIcon } from '@/components/icon';
import { Text } from '@/components/text';
import { ENABLE_CAMPAIGNS, EXIT_MODAL_HREF_COOKIE } from '@/constants';

import { Loader } from '../loader';

import { handleButtonClickAnalytics } from './analytics';
import {
  buttonIconVariants,
  buttonVariants,
  buttonSizeVariants,
} from './style';

import type { ButtonVariants, ButtonSizeVariants } from './style';
import type { ButtonHTMLAttributes, PropsWithChildren, JSX } from 'react';

type ButtonProps = PropsWithChildren<{
  ariaLabel?: string;
  className?: string;
  href?: LinkProps['href'];
  onClick?: () => void;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  form?: ButtonHTMLAttributes<HTMLButtonElement>['form'];
  variant?: ButtonVariants['variant'];
  icon?: JSX.Element | string;
  iconPosition?: ButtonVariants['iconPosition'];
  size?: ButtonSizeVariants['size'];
  disabled?: ButtonHTMLAttributes<HTMLButtonElement>['disabled'];
  target?: string;
  download?: boolean;
  loading?: boolean;
  'data-testid'?: string;
  count?: number;
}>;

type IconComponentProps = Pick<
  ButtonProps,
  'icon' | 'variant' | 'size' | 'loading' | 'count'
>;
type ContentProps = PropsWithChildren & IconComponentProps;

const IconComponent = ({
  icon,
  variant,
  size = 'default',
}: IconComponentProps) => {
  switch (typeof icon) {
    case 'string':
      return (
        <ContentfulIcon
          id={icon}
          className={`size-icon ${buttonIconVariants({ variant })}`}
        />
      );
    default:
      return (
        <Icon
          className={buttonIconVariants({ variant })}
          icon={icon}
          size={size}
        />
      );
  }
};

const Content = ({
  children,
  variant,
  icon,
  size,
  loading,
  count,
}: ContentProps) => {
  if (loading) {
    return (
      <Loader
        size={24}
        color="#FFF"
      />
    );
  }

  return (
    <>
      {icon ? (
        <IconComponent
          icon={icon}
          variant={variant}
          size={size}
        />
      ) : null}
      {count !== undefined && count > 0 && (
        <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#ff0000] text-[14px] font-semibold text-white">
          {count}
        </span>
      )}
      {variant === 'tertiary' ? (
        <Text
          as="span"
          variant="button"
          className="border-b-2 border-b-button-tertiary-underline-default group-hover:border-b-button-tertiary-underline-hover group-focus-visible:border-b-button-tertiary-underline-focused group-active:border-b-button-tertiary-underline-pressed group-disabled:border-b-button-tertiary-underline-disabled"
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </>
  );
};

export const Button = ({
  ariaLabel,
  className,
  href,
  children,
  onClick,
  variant = 'primary',
  type = 'button',
  icon,
  iconPosition = 'left',
  disabled = false,
  target,
  form,
  size = 'default',
  download = false,
  loading = false,
  'data-testid': dataTestId,
  count,
}: ButtonProps) => {
  const classes = clsx([
    buttonVariants({ variant, iconPosition }),
    buttonSizeVariants({ size }),
    className,
    'relative',
  ]);

  const pathname = usePathname();
  const isCampaignPageType = useIsCampaignPageType();

  if (href) {
    if (ENABLE_CAMPAIGNS && isCampaignPageType && !pathname.includes('/exit')) {
      return (
        <Link
          href={`${pathname}/exit`}
          scroll={false}
          aria-label={ariaLabel}
          className={classes}
          {...(target && { target })}
          {...(download && { download })}
          onMouseDown={() => {
            setCookie(EXIT_MODAL_HREF_COOKIE, href as string);
          }}
          onClick={() => {
            setCookie(EXIT_MODAL_HREF_COOKIE, href as string);
            if (onClick) {
              onClick();
            }
            handleButtonClickAnalytics(href, pathname);
          }}
          data-testid={dataTestId || undefined}
        >
          {children}
        </Link>
      );
    }

    return (
      <Link
        aria-label={ariaLabel}
        className={classes}
        href={href}
        {...(target && { target })}
        {...(download && { download })}
        onClick={() => {
          if (onClick) {
            onClick();
          }
          handleButtonClickAnalytics(href, pathname);
        }}
        data-testid={dataTestId || undefined}
        scroll={!pathname.includes('/modals/')}
      >
        <Content
          icon={icon}
          size={size}
          variant={variant}
          loading={loading}
          count={count}
        >
          {children}
        </Content>
      </Link>
    );
  }

  return (
    <button
      aria-label={ariaLabel}
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      form={form}
      {...(download && { download })}
      data-testid={dataTestId || undefined}
    >
      <Content
        icon={icon}
        size={size}
        variant={variant}
        loading={loading}
        count={count}
      >
        {children}
      </Content>
    </button>
  );
};

Button.displayName = 'Button';
